import { useCallback, useEffect, useState } from 'react';
import { get, post } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_ADMIN_AUDIT_LOG } from '../data/mock';
import { currentLocalDayIsoRange } from '../utils/local-date-time-range';

export type CrowdAdminAuditReviewAction = 'reviewed' | 'flagged' | 'unflagged' | 'commented';
export type CrowdAdminAuditExportFormat = 'pdf' | 'csv';

export interface CrowdAdminAuditLogReview {
  id: string;
  auditLogId: string;
  reviewAction: CrowdAdminAuditReviewAction;
  reviewedBy: string;
  reviewedAt: string;
  reviewerRoleSnapshot: Record<string, unknown>;
}

export interface CrowdAdminAuditLogEntry {
  id: string;
  occurredAt: string;
  actor: string;
  actorRole: string | null;
  actorType: 'human' | 'service_account' | 'system_cron' | 'anonymous';
  actorRoleSnapshot: Record<string, unknown>;
  action: string;
  resourceType: string;
  resourceId: string;
  outcome: 'success' | 'failure' | 'denied';
  correlationId: string | null;
  sessionId: string | null;
  sourceIpHash: string | null;
  userAgentHash: string | null;
  userAgentFamily: string | null;
  clientType: string | null;
  operationId: string | null;
  httpMethod: string | null;
  endpointPath: string | null;
  statusCode: number | null;
  queryParams: Record<string, unknown>;
  previousState: Record<string, unknown> | null;
  newState: Record<string, unknown> | null;
  failureReasonCode: string | null;
  failureReasonDetail: string | null;
  hashAlgorithm: string | null;
  previousRowHash: string | null;
  rowHash: string | null;
  latestReview: CrowdAdminAuditLogReview | null;
  synthetic?: boolean;
  target: string;
  detail: string;
}

export interface CrowdAdminAuditLogFilters {
  actor?: string;
  actorRole?: string;
  action?: string;
  resourceType?: string;
  resourceId?: string;
  outcome?: 'success' | 'failure' | 'denied';
  correlationId?: string;
  sessionId?: string;
  sourceIpHash?: string;
  actorType?: string;
  operationId?: string;
  httpMethod?: string;
  endpointPath?: string;
  statusCode?: number | string;
  failureReasonCode?: string;
  search?: string;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export interface CrowdAdminAuditLogFilterOptions {
  actions: string[];
  resourceTypes: string[];
  outcomes: Array<'success' | 'failure' | 'denied'>;
  actorRoles: string[];
  actorTypes: string[];
  httpMethods: string[];
}

export interface CrowdAdminAuditLogResponse {
  applicationId: 'crowd_monitoring';
  generatedAt: string;
  page: number;
  pageSize: number;
  total: number;
  hasNextPage: boolean;
  filters: CrowdAdminAuditLogFilters;
  filterOptions: CrowdAdminAuditLogFilterOptions;
  entries: CrowdAdminAuditLogEntry[];
}

export type CrowdAdminAuditIntegrityIssueType =
  | 'unsupported_algorithm'
  | 'missing_hash'
  | 'previous_hash_mismatch'
  | 'row_hash_mismatch';

export interface CrowdAdminAuditIntegrityResponse {
  applicationId: 'crowd_monitoring';
  valid: boolean;
  algorithm: string;
  checkedRows: number;
  unverifiedRows: number;
  firstCheckedAt: string | null;
  lastCheckedAt: string | null;
  terminalRowHash: string | null;
  firstIssue: {
    type: CrowdAdminAuditIntegrityIssueType;
    auditLogId: string;
    expected: string | null;
    actual: string | null;
  } | null;
  verifiedAt: string;
}

interface CrowdAdminAuditReviewEnvelope {
  applicationId: 'crowd_monitoring';
  review: CrowdAdminAuditLogReview;
}

export interface CrowdAdminAuditExportJob {
  id?: string;
  jobId: string;
  templateId: 'audit_log_export';
  status: string;
  format: CrowdAdminAuditExportFormat;
  requestedAt: string;
  completedAt: string | null;
  outputHash: string | null;
  outputSizeBytes: number | null;
  errorMessage: string | null;
  parameters: Record<string, unknown>;
  downloadUrl?: string;
}

interface AdminAuditLogHookState {
  data: CrowdAdminAuditLogResponse | null;
  loading: boolean;
  error: string | null;
  integrity: CrowdAdminAuditIntegrityResponse | null;
  verifying: boolean;
  filters: CrowdAdminAuditLogFilters;
  setFilters: (filters: Partial<CrowdAdminAuditLogFilters>) => void;
  refresh: (nextFilters?: Partial<CrowdAdminAuditLogFilters>) => Promise<void>;
  reviewAuditLog: (id: string, action: CrowdAdminAuditReviewAction) => Promise<void>;
  verifyIntegrity: () => Promise<void>;
  requestAuditExport: (format: CrowdAdminAuditExportFormat, operationId: string) => Promise<CrowdAdminAuditExportJob>;
}

function defaultAuditLogFilters(): CrowdAdminAuditLogFilters {
  const range = currentLocalDayIsoRange();
  return {
    page: 1,
    pageSize: 25,
    from: range.from,
    to: range.to,
  };
}

function unwrap<T>(body: T | { data?: T }): T {
  const b = body as { data?: T };
  return (b.data !== undefined ? b.data : body) as T;
}

export function useAdminAuditLog(): AdminAuditLogHookState {
  const { isMock } = useMockMode();
  const [data, setData] = useState<CrowdAdminAuditLogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrity, setIntegrity] = useState<CrowdAdminAuditIntegrityResponse | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [filters, setFilterState] = useState<CrowdAdminAuditLogFilters>(() => defaultAuditLogFilters());

  const setFilters = useCallback((next: Partial<CrowdAdminAuditLogFilters>) => {
    setFilterState(prev => ({ ...prev, ...next }));
  }, []);

  const refresh = useCallback(async (nextFilters: Partial<CrowdAdminAuditLogFilters> = {}) => {
    const queryFilters = { ...filters, ...nextFilters };
    setLoading(true);
    setError(null);
    try {
      if (isMock) {
        await new Promise(resolve => setTimeout(resolve, 40));
        setData(filterMockAudit(MOCK_ADMIN_AUDIT_LOG, queryFilters));
      } else {
        const body = await get<CrowdAdminAuditLogResponse | { data?: CrowdAdminAuditLogResponse }>(
          `/crowd/admin/audit-log?${queryString(queryFilters)}`,
        );
        setData(unwrap(body));
      }
    } catch {
      setError('Failed to load audit log');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filters, isMock]);

  const reviewAuditLog = useCallback(async (id: string, action: CrowdAdminAuditReviewAction) => {
    if (isMock) {
      const review: CrowdAdminAuditLogReview = {
        id: `mock-review-${id}-${Date.now()}`,
        auditLogId: id,
        reviewAction: action,
        reviewedBy: 'system-admin',
        reviewedAt: new Date().toISOString(),
        reviewerRoleSnapshot: { role: 'system_admin' },
      };
      setData(prev => prev ? {
        ...prev,
        entries: prev.entries.map(entry => entry.id === id ? { ...entry, latestReview: review } : entry),
      } : prev);
      return;
    }
    const body = await post<CrowdAdminAuditReviewEnvelope | { data?: CrowdAdminAuditReviewEnvelope }>(
      `/crowd/admin/audit-log/${encodeURIComponent(id)}/reviews`,
      { action },
    );
    const review = unwrap<CrowdAdminAuditReviewEnvelope>(body).review;
    setData(prev => prev ? {
      ...prev,
      entries: prev.entries.map(entry => entry.id === id ? { ...entry, latestReview: review } : entry),
    } : prev);
  }, [isMock]);

  const verifyIntegrity = useCallback(async () => {
    setVerifying(true);
    try {
      if (isMock) {
        const entries = data?.entries ?? MOCK_ADMIN_AUDIT_LOG.entries;
        setIntegrity({
          applicationId: 'crowd_monitoring',
          valid: true,
          algorithm: 'sha256',
          checkedRows: entries.length,
          unverifiedRows: 0,
          firstCheckedAt: entries[entries.length - 1]?.occurredAt ?? null,
          lastCheckedAt: entries[0]?.occurredAt ?? null,
          terminalRowHash: entries[0]?.rowHash ?? 'mock-terminal-row-hash',
          firstIssue: null,
          verifiedAt: new Date().toISOString(),
        });
        return;
      }
      const body = await get<CrowdAdminAuditIntegrityResponse | { data?: CrowdAdminAuditIntegrityResponse }>(
        '/crowd/admin/audit-log/integrity',
      );
      setIntegrity(unwrap(body));
    } finally {
      setVerifying(false);
    }
  }, [data?.entries, isMock]);

  const requestAuditExport = useCallback(async (
    format: CrowdAdminAuditExportFormat,
    operationId: string,
  ): Promise<CrowdAdminAuditExportJob> => {
    const scopedOperationId = operationId.trim();
    if (!scopedOperationId) throw new Error('operationId is required for audit export');
    const { page: _page, pageSize: _pageSize, ...exportFilters } = filters;
    if (isMock) {
      return {
        id: `audit-export-${Date.now()}`,
        jobId: `audit-export-${Date.now()}`,
        templateId: 'audit_log_export',
        status: 'queued',
        format,
        requestedAt: new Date().toISOString(),
        completedAt: null,
        outputHash: null,
        outputSizeBytes: null,
        errorMessage: null,
        parameters: {
          operationId: scopedOperationId,
          auditFilters: { ...exportFilters, operationId: scopedOperationId },
        },
      };
    }
    const body = await post<CrowdAdminAuditExportJob | { data?: CrowdAdminAuditExportJob }>('/reports', {
      templateId: 'audit_log_export',
      format,
      parameters: {
        operationId: scopedOperationId,
        auditFilters: { ...exportFilters, operationId: scopedOperationId },
      },
    });
    return unwrap(body);
  }, [filters, isMock]);

  useEffect(() => { refresh(); }, [refresh]);

  return {
    data,
    loading,
    error,
    integrity,
    verifying,
    filters,
    setFilters,
    refresh,
    reviewAuditLog,
    verifyIntegrity,
    requestAuditExport,
  };
}

function queryString(filters: CrowdAdminAuditLogFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && String(value).trim()) {
      params.set(key, String(value));
    }
  }
  return params.toString();
}

function filterMockAudit(
  data: CrowdAdminAuditLogResponse,
  filters: CrowdAdminAuditLogFilters,
): CrowdAdminAuditLogResponse {
  const search = filters.search?.toLowerCase().trim();
  const filtered = data.entries.filter(entry => {
    if (filters.actor && !entry.actor.toLowerCase().includes(filters.actor.toLowerCase())) return false;
    if (filters.actorRole && entry.actorRole !== filters.actorRole) return false;
    if (filters.action && entry.action !== filters.action) return false;
    if (filters.resourceType && entry.resourceType !== filters.resourceType) return false;
    if (filters.resourceId && entry.resourceId !== filters.resourceId) return false;
    if (filters.outcome && entry.outcome !== filters.outcome) return false;
    if (filters.correlationId && entry.correlationId !== filters.correlationId) return false;
    if (filters.sessionId && entry.sessionId !== filters.sessionId) return false;
    if (filters.sourceIpHash && entry.sourceIpHash !== filters.sourceIpHash) return false;
    if (filters.actorType && entry.actorType !== filters.actorType) return false;
    if (filters.operationId && entry.operationId !== filters.operationId) return false;
    if (filters.httpMethod && entry.httpMethod !== filters.httpMethod) return false;
    if (filters.endpointPath && entry.endpointPath !== filters.endpointPath) return false;
    if (filters.statusCode && String(entry.statusCode ?? '') !== String(filters.statusCode)) return false;
    if (filters.failureReasonCode && entry.failureReasonCode !== filters.failureReasonCode) return false;
    const occurredAt = Date.parse(entry.occurredAt);
    if (filters.from) {
      const from = Date.parse(filters.from);
      if (Number.isFinite(from) && Number.isFinite(occurredAt) && occurredAt < from) return false;
    }
    if (filters.to) {
      const to = Date.parse(filters.to);
      if (Number.isFinite(to) && Number.isFinite(occurredAt) && occurredAt > to) return false;
    }
    if (search && !`${entry.actor} ${entry.action} ${entry.resourceType} ${entry.target} ${entry.detail} ${entry.operationId ?? ''} ${entry.failureReasonCode ?? ''}`.toLowerCase().includes(search)) {
      return false;
    }
    return true;
  });
  const page = Math.max(filters.page, 1);
  const pageSize = Math.max(filters.pageSize, 1);
  const offset = (page - 1) * pageSize;
  return {
    ...data,
    page,
    pageSize,
    total: filtered.length,
    hasNextPage: offset + pageSize < filtered.length,
    filters,
    entries: filtered.slice(offset, offset + pageSize),
  };
}
